'use client';

import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.13]"></div>
      </div>

      <Header />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors group mb-8 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back</span>
        </Link>

        <article className="prose prose-invert prose-neutral max-w-none">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent mb-4">
            Privacy Policy
          </h1>
          <p className="text-neutral-400 text-sm mb-8">Effective date: June 2026</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">1. Overview</h2>
            <p className="text-neutral-300 leading-relaxed">
              This policy explains what data the Silker AI SDK (<code className="text-rose-400">@silker-ai/agent</code>) and the Silker AI dashboard collect and process when you use our runtime security product, why we process it, how long we keep it, and the rights you have. Silker AI provides runtime threat detection that runs inside your own application or proxy; we receive only the security telemetry described below.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">2. What the SDK Collects</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              The SDK inspects traffic locally inside your application. It transmits telemetry to Silker AI primarily for flagged or suspicious traffic, including:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li><strong className="text-white">HTTP request metadata:</strong> method, path, status code, timing, and route classification</li>
              <li><strong className="text-white">Network identifiers:</strong> IP addresses and user agents</li>
              <li><strong className="text-white">Request context for flagged traffic:</strong> relevant request bodies and headers associated with a detected threat</li>
              <li><strong className="text-white">Detection results:</strong> what was flagged, the rule that matched, and the action taken</li>
            </ul>
            <p className="text-neutral-300 leading-relaxed mt-4">
              <strong className="text-white">PII sanitization is applied client-side</strong> inside the SDK before telemetry leaves your servers. Sensitive values (such as emails, tokens, card numbers, and other personal data) are redacted prior to transmission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Data</h2>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li>Detecting, classifying, and blocking threats in real time</li>
              <li>Providing security analytics and dashboards to you, the customer</li>
              <li>Maintaining, securing, and improving the reliability of the service</li>
              <li>Investigating abuse and responding to security incidents</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">4. Storage</h2>
            <p className="text-neutral-300 leading-relaxed">
              Security telemetry is stored on secure, EU-hosted cloud infrastructure with access controls and encryption in transit. Tenant data is isolated so that each customer can access only their own data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">5. Data Retention</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              Retention of security event data depends on your plan:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li><strong className="text-white">Starter:</strong> 7 days</li>
              <li><strong className="text-white">Pro:</strong> 30 days</li>
              <li><strong className="text-white">Business:</strong> 90 days</li>
            </ul>
            <p className="text-neutral-300 leading-relaxed mt-4">
              After the retention window, event data is deleted or anonymized. Account and billing records are kept only as long as required for legal and accounting purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">6. Sharing &amp; Subprocessors</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              We do not sell personal data. We share data only with subprocessors that help us deliver the service:
            </p>
            <ul className="list-disc list-inside text-neutral-300 space-y-2 ml-4">
              <li><strong className="text-white">Cloud hosting:</strong> EU-hosted infrastructure for storage and processing</li>
              <li><strong className="text-white">Stripe:</strong> billing and payment processing</li>
              <li><strong className="text-white">OpenAI:</strong> AI analysis of flagged threats</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">7. Customer Responsibilities</h2>
            <p className="text-neutral-300 leading-relaxed">
              When you deploy Silker AI to monitor traffic to your application, you are responsible for disclosing this monitoring in your own privacy policy and for obtaining any consent required under applicable law. As the controller of your end-users&apos; data, you determine what traffic is processed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">8. Your GDPR Rights</h2>
            <p className="text-neutral-300 leading-relaxed mb-4">
              Subject to applicable law, you have the right to access, correct, delete, restrict, or port your personal data, and to object to processing. To exercise these rights, contact us and we will respond in line with our obligations.
            </p>
            <p className="text-neutral-300 leading-relaxed">
              Requests for access or deletion can be made via support at{' '}
              <a href="mailto:privacy@silkerai.com" className="text-rose-400 hover:text-rose-300 transition">
                privacy@silkerai.com
              </a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">9. Changes</h2>
            <p className="text-neutral-300 leading-relaxed">
              We may update this policy from time to time. Material changes will be reflected by updating the effective date above.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">10. Contact</h2>
            <p className="text-neutral-300 leading-relaxed">
              Questions about this policy? Contact us at{' '}
              <a href="mailto:privacy@silkerai.com" className="text-rose-400 hover:text-rose-300 transition">
                privacy@silkerai.com
              </a>.
            </p>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
